<template>
  <div class="flex-1">
    <label for="phone" class="block text-sm text-gray-700">
      {{ $t("Customer phone number") }}
      <span class="ml-1 text-red-600">*</span>
    </label>
    <div class="flex">
      <!-- Calling Code Dropdown -->
      <div class="relative">
        <!-- Hidden field for calling code validation -->
        <Field name="customer.callingCode" type="hidden" :value="selectedCallingCode" />
        <Listbox v-model="selectedCallingCode">
          <ListboxButton
            class="block w-full rounded-l-md border-0 border-r-0 py-1.5 pl-3 pr-7 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 cursor-pointer relative"
            @keydown="blockEnterBehavior"
          >
            <span class="block text-left">{{ selectedCountry?.flag }}</span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>

          <transition
            leave-active-class="transition ease-in duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <ListboxOptions
              class="absolute z-10 mt-1 max-h-60 min-w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-inset ring-gray-300 focus:outline-hidden sm:text-sm"
            >
              <ListboxOption
                as="template"
                v-for="country in countries"
                :key="country.code"
                :value="country.callingCode"
                v-slot="{ active, selected }"
              >
                <li
                  :class="[
                    selected ? 'text-white bg-primary' : 'text-gray-900',
                    active ? 'bg-gray-100' : '',
                    'relative select-none py-2 pl-3 pr-3 cursor-pointer text-xs',
                  ]"
                >
                  <span :class="[selected ? 'font-medium' : 'font-normal', 'block whitespace-nowrap']">
                    {{ country.flag }} {{ country.name }}
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </Listbox>
      </div>

      <!-- Phone Number Input -->
      <Field
        id="phone"
        name="customer.phone"
        type="tel"
        class="block w-full rounded-r-md border-0 py-1.5 pl-3 pr-3 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 flex-1"
        :placeholder="phonePlaceholder"
        v-model="fullPhoneNumber"
        @keydown="handleKeyDown"
        @beforeinput="handleBeforeInput"
        @input="handleInput"
        @paste="handlePaste"
        @compositionstart="handleComposition"
        @compositionupdate="handleComposition"
        @compositionend="handleComposition"
      />
    </div>
    <ErrorMessage name="customer.phone" class="mt-2 text-sm text-red-600" />
  </div>
</template>

<script>
import { Field, ErrorMessage } from "vee-validate";
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/solid";
import { blockEnterBehavior } from "@/utils/blockEnterBehavior";

export default {
  name: "PhoneNumberFormField",
  components: {
    Field,
    ErrorMessage,
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    ChevronDownIcon,
  },
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    shippingAddress: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      selectedCallingCode: "+33",
      phoneNumber: "",
      isApplyingCanonical: false,
      countries: [
        { code: "FR", name: "France", callingCode: "+33", flag: "\u{1F1EB}\u{1F1F7}" },
        { code: "GP", name: "Guadeloupe", callingCode: "+590", flag: "\u{1F1EC}\u{1F1F5}" },
        { code: "MQ", name: "Martinique", callingCode: "+596", flag: "\u{1F1F2}\u{1F1F6}" },
        { code: "GF", name: "Guyane Française", callingCode: "+594", flag: "\u{1F1EC}\u{1F1EB}" },
        { code: "RE", name: "Réunion", callingCode: "+262", flag: "\u{1F1F7}\u{1F1EA}" },
        { code: "BE", name: "Belgique", callingCode: "+32", flag: "\u{1F1E7}\u{1F1EA}" },
        { code: "CH", name: "Suisse", callingCode: "+41", flag: "\u{1F1E8}\u{1F1ED}" },
      ],
    };
  },
  computed: {
    rawPhoneFromModel() {
      return this.modelValue || "";
    },
    selectedCountry() {
      return this.countries.find((c) => c.callingCode === this.selectedCallingCode);
    },
    selectedCountryCode() {
      return this.selectedCountry?.code;
    },
    currentZipCode() {
      return this.shippingAddress?.zipCode;
    },
    currentCountryCode() {
      return this.shippingAddress?.country || "FR";
    },
    phonePlaceholder() {
      if (this.selectedCountryCode) {
        try {
          const example = getExampleNumber(this.selectedCountryCode, examples);
          if (example) return example.formatNational().replace(/^0/, "").trim();
        } catch (e) {
          // Fallback to generic placeholder
        }
      }
      return "6 99 49 75 90";
    },
    fullPhoneNumber: {
      get() {
        return this.selectedCallingCode ? `${this.selectedCallingCode} ${this.phoneNumber}` : "";
      },
      set(value) {
        if (!value) {
          this.phoneNumber = "";
          return;
        }

        if (value?.startsWith(this.selectedCallingCode)) {
          this.phoneNumber = value
            .replace(this.selectedCallingCode, "")
            .trim()
            .replace(/^0(?=.{9}$)/, "");
        } else {
          // If the value doesn't start with the calling code, try to preserve it
          const match = value.match(/^(\+\d{1,4})/);
          if (match) {
            const callingCode = match[1];
            const foundCountry = this.countries.find((c) => c.callingCode === callingCode);
            if (foundCountry) {
              this.selectedCallingCode = callingCode;
              this.phoneNumber = value
                .replace(callingCode, "")
                .trim()
                .replace(/^0(?=.{9}$)/, "");
            } else {
              this.phoneNumber = value
                .replace(/^\+\d{1,4}\s?/, "")
                .trim()
                .replace(/^0(?=.{9}$)/, "");
            }
          } else {
            this.phoneNumber = value.trim().replace(/^0(?=.{9}$)/, "");
          }
        }
      },
    },
  },
  beforeMount() {
    this.syncFromModel();
  },
  methods: {
    getInitialCallingCode() {
      if (this.currentCountryCode === "FR" && this.currentZipCode) {
        const zipPrefix = String(this.currentZipCode).substring(0, 3);

        switch (zipPrefix) {
          case "971":
            return "+590"; // Guadeloupe
          case "972":
            return "+596"; // Martinique
          case "973":
            return "+594"; // Guyane
          case "974":
            return "+262"; // Réunion
          case "976":
            return "+262"; // Mayotte
          default:
            return "+33"; // Métropole
        }
      } else {
        const country = this.countries.find((c) => c.code === this.currentCountryCode);
        return country ? country.callingCode : "+33";
      }
    },

    expectedPrefix() {
      return `${this.selectedCallingCode} `;
    },

    digitsFromRaw(raw) {
      return String(raw || "")
        .replace(/^\+\d{1,4}\s?/, "")
        .replace(/\D/g, "");
    },

    applyCanonicalValue(inputEl, rawValue, { cursor = "end" } = {}) {
      if (this.isApplyingCanonical) return;

      const prefix = this.expectedPrefix();
      const digits = this.digitsFromRaw(rawValue);
      const corrected = prefix + digits;

      if (rawValue === corrected && inputEl.value === corrected) return;

      this.isApplyingCanonical = true;
      this.fullPhoneNumber = corrected;
      inputEl.value = corrected;
      this.$nextTick(() => {
        this.isApplyingCanonical = false;

        requestAnimationFrame(() => {
          const pos = cursor === "end" ? corrected.length : prefix.length;
          inputEl.setSelectionRange(pos, pos);
        });
      });
    },

    blockEnterBehavior,

    handleKeyDown(event) {
      this.blockEnterBehavior(event);

      const allowedKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
      if (allowedKeys.includes(event.key)) return;

      if (event.ctrlKey || event.metaKey) return;

      if (event.key === "Dead") {
        event.preventDefault();
        return;
      }

      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    },

    handleBeforeInput(event) {
      const input = event.target;
      const prefixLen = this.expectedPrefix().length;

      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const inputType = event.inputType || "";
      const isDeletion = inputType.startsWith("delete");

      if (isDeletion) {
        if (end <= prefixLen) {
          event.preventDefault();
          requestAnimationFrame(() => input.setSelectionRange(prefixLen, prefixLen));
          return;
        }

        if (start < prefixLen && end > prefixLen) {
          event.preventDefault();
          const raw = input.value || "";
          const corrected = raw.slice(0, prefixLen) + raw.slice(end);
          input.value = corrected;
          input.dispatchEvent(new Event("input", { bubbles: true }));
          requestAnimationFrame(() => input.setSelectionRange(prefixLen, prefixLen));
          return;
        }

        return;
      }

      if (start < prefixLen) {
        event.preventDefault();
        requestAnimationFrame(() => input.setSelectionRange(prefixLen, prefixLen));
        return;
      }

      if (typeof event.data === "string" && event.data.length > 0 && /[^0-9]/.test(event.data)) {
        event.preventDefault();
      }
    },

    handleInput(event) {
      const input = event.target;
      this.applyCanonicalValue(input, input.value, { cursor: "end" });
    },

    handlePaste(event) {
      event.preventDefault();
      const input = event.target;

      const pastedText = (event.clipboardData || window.clipboardData).getData("text");
      const digits = this.digitsFromRaw(pastedText);

      const prefix = this.expectedPrefix();
      const raw = input.value || prefix;

      const prefixLen = prefix.length;
      const start = Math.max(prefixLen, input.selectionStart ?? prefixLen);
      const end = Math.max(prefixLen, input.selectionEnd ?? prefixLen);

      const before = raw.slice(0, start);
      const after = raw.slice(end);
      const nextRaw = before + digits + after;

      this.applyCanonicalValue(input, nextRaw, { cursor: "end" });
    },

    handleComposition(event) {
      const input = event.target;
      this.applyCanonicalValue(input, input.value, { cursor: "end" });
    },
    updateModel() {
      this.$emit("update:modelValue", this.fullPhoneNumber.replace(/ /g, ""));
    },

    syncFromModel() {
      const raw = this.rawPhoneFromModel?.trim?.();
      if (raw) {
        this.fullPhoneNumber = raw;
      } else {
        this.selectedCallingCode = this.getInitialCallingCode();
      }
      this.updateModel();
    },
  },
  watch: {
    selectedCallingCode() {
      this.updateModel();
    },
    phoneNumber() {
      this.updateModel();
    },
  },
};
</script>
